import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'antd';
import { DownOutlined, AppstoreOutlined } from '@ant-design/icons';
import { AnimatePresence, LayoutGroup, motion, useMotionValueEvent, useReducedMotion, useScroll, useSpring } from 'framer-motion';

// Màn rộng (≥1280px, đủ chỗ ngoài nội dung): cuộn xuống thì thanh menu ngang trượt sang mép phải thành dock dọc (như thanh tab dọc của app trên máy gập),
// cuộn lên đầu trang thì trở lại ngang. Hai dạng dùng chung layoutId nên framer-motion tự biến hình giữa chúng.
// Ngưỡng có độ trễ (160 / 100px) để không nhấp nháy khi dừng quanh ngưỡng. Điện thoại giữ nguyên thanh ngang
const DOCK_AT = 160;
const UNDOCK_AT = 100;
// Lò xo mềm cho biến hình ngang ↔ dọc; hai dạng mờ chéo nhau trong lúc chuyển
const MORPH = { type: 'spring', stiffness: 170, damping: 24, mass: 1 };
const FADE = { duration: 0.28, ease: [0.22, 1, 0.36, 1] };

const useDocked = () => {
    const { scrollY } = useScroll();
    const [scrolled, setScrolled] = useState(() => window.scrollY > DOCK_AT);
    const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1280px)').matches);
    useEffect(() => {
        const mq = window.matchMedia('(min-width: 1280px)');
        const onChange = () => setIsDesktop(mq.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    useMotionValueEvent(scrollY, 'change', (v) => setScrolled((was) => (was ? v > UNDOCK_AT : v > DOCK_AT)));
    return { docked: isDesktop && scrolled, isDesktop };
};

// Màn hẹp (điện thoại, máy tính bảng – không có dock): cuộn quá 400px thì hiện nút về đầu trang ở góc phải dưới,
// vòng quanh nút là tiến độ đã cuộn của trang
const BackToTop = () => {
    const { scrollY, scrollYProgress } = useScroll();
    const [show, setShow] = useState(false);
    useMotionValueEvent(scrollY, 'change', (v) => setShow(v > 400));
    const progress = useSpring(scrollYProgress, { stiffness: 200, damping: 30 });
    return (
        <AnimatePresence>
            {show ? (
                <motion.button
                    key="top"
                    type="button"
                    aria-label="Về đầu trang"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    initial={{ opacity: 0, scale: 0.6, y: 16 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.6, y: 16 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 24 }}
                    className="fixed right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-xl shadow-primary/30"
                    style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}>
                    {/* vòng tiến độ cuộn */}
                    <svg viewBox="0 0 56 56" className="absolute inset-0 -rotate-90" aria-hidden="true">
                        <circle cx="28" cy="28" r="25" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="3" />
                        <motion.circle cx="28" cy="28" r="25" fill="none" stroke="#FF4B2B" strokeWidth="3" strokeLinecap="round" style={{ pathLength: progress }} />
                    </svg>
                    <span className="material-symbols-outlined relative text-2xl">arrow_upward</span>
                </motion.button>
            ) : null}
        </AnimatePresence>
    );
};

// Mục trong dock: icon + nhãn ngắn bên dưới
const DockItem = ({ icon, label, href, onClick, index, primary = false }) => (
    <motion.a
        href={href}
        onClick={onClick}
        initial={{ opacity: 0, y: 10, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 26, delay: 0.12 + index * 0.035 }}
        className={`group flex w-16 flex-col items-center gap-0.5 rounded-2xl py-2 text-[10px] font-bold transition-colors ${
            primary
                ? 'bg-primary text-white shadow-lg shadow-primary/30 hover:bg-primary/90 hover:text-white'
                : 'text-slate-500 hover:bg-slate-100 hover:text-accent dark:text-slate-300 dark:hover:bg-slate-800'
        }`}
    >
        <span className="material-symbols-outlined text-[22px] transition-transform group-hover:scale-110">{icon}</span>
        {label}
    </motion.a>
);

const LandingHeader = () => {
    const navigate = useNavigate();
    const { docked, isDesktop } = useDocked();
    const reduce = useReducedMotion();
    const go = (path) => (e) => {
        e.preventDefault();
        navigate(path);
    };

    const toolItems = [
        {
            key: '0',
            label: (
                <div
                    onClick={() => navigate('/cong-cu')}
                    className="flex items-center py-1 cursor-pointer font-bold text-primary border-b border-slate-100 pb-2 mb-1"
                >
                    Xem tất cả công cụ miễn phí &rarr;
                </div>
            ),
        },
        {
            key: '1',
            label: (
                <div onClick={() => navigate('/cong-cu/tra-cuu-vtf')} className="flex items-center gap-2 py-1 cursor-pointer group">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-500 group-hover:bg-indigo-500 group-hover:text-white transition-all">
                        <span className="material-symbols-outlined text-lg">person_search</span>
                    </div>
                    <span className="font-bold text-slate-700 group-hover:text-indigo-500 transition-colors">Tra cứu mã VTF</span>
                </div>
            ),
        },
        {
            key: '3',
            label: (
                <div onClick={() => navigate('/cong-cu/tao-ma-qr')} className="flex items-center gap-2 py-1 cursor-pointer group">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-all">
                        <span className="material-symbols-outlined text-lg">qr_code_scanner</span>
                    </div>
                    <span className="font-bold text-slate-700 group-hover:text-primary transition-colors">Tạo mã QR vĩnh viễn</span>
                </div>
            ),
        },
        {
            key: '4',
            label: (
                <div onClick={() => navigate('/cong-cu/vong-quay-may-man')} className="flex items-center gap-2 py-1 cursor-pointer group">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-500/10 text-rose-500 group-hover:bg-rose-500 group-hover:text-white transition-all">
                        <span className="material-symbols-outlined text-lg">pie_chart</span>
                    </div>
                    <span className="font-bold text-slate-700 group-hover:text-rose-500 transition-colors">Vòng quay may mắn</span>
                </div>
            ),
        },
        {
            key: '5',
            label: (
                <div onClick={() => navigate('/cong-cu/tra-cuu-ket-qua-thi')} className="flex items-center gap-2 py-1 cursor-pointer group">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-500 group-hover:bg-indigo-500 group-hover:text-white transition-all">
                        <span className="material-symbols-outlined text-lg">verified</span>
                    </div>
                    <span className="font-bold text-slate-700 group-hover:text-indigo-500 transition-colors">Tra cứu kết quả thi</span>
                </div>
            ),
        },
    ];

    const parentItem = {
        key: 'parent',
        label: (
            <div onClick={() => navigate('/tra-cuu-phu-huynh')} className="flex items-center gap-2 py-1 cursor-pointer group">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10 text-accent group-hover:bg-accent group-hover:text-white transition-all">
                    <span className="material-symbols-outlined text-lg">family_restroom</span>
                </div>
                <span className="font-bold text-slate-700 group-hover:text-accent transition-colors">Cổng phụ huynh</span>
            </div>
        ),
    };

    const shell = 'border border-slate-200/50 bg-white/70 backdrop-blur-xl shadow-sm dark:bg-slate-900/70 dark:border-slate-800';

    return (
        <LayoutGroup>
            <AnimatePresence initial={false}>
                {docked ? (
                    <motion.aside key="dock" className="pointer-events-none fixed inset-y-0 right-4 z-50 flex items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={FADE}>
                        <motion.nav
                            layoutId={reduce ? undefined : 'landing-nav'}
                            transition={MORPH}
                            style={{ borderRadius: 32 }}
                            aria-label="Menu"
                            className={`pointer-events-auto flex flex-col items-center gap-1 p-2 shadow-xl shadow-primary/10 ${shell}`}
                        >
                            <motion.button
                                type="button"
                                aria-label="Về đầu trang"
                                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.05, duration: 0.3 }}
                                className="mb-1 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white"
                            >
                                <span className="material-symbols-outlined">sports_martial_arts</span>
                            </motion.button>
                            <DockItem index={0} icon="auto_awesome" label="Tính năng" href="/#features" />
                            <DockItem index={1} icon="forum" label="HLV" href="/#testimonials" />
                            <DockItem index={2} icon="family_restroom" label="Phụ huynh" href="/tra-cuu-phu-huynh" onClick={go('/tra-cuu-phu-huynh')} />
                            <DockItem index={3} icon="help" label="Hỏi đáp" href="/#faq" />
                            <Dropdown menu={{ items: toolItems }} trigger={['hover']} placement="leftTop">
                                <span>
                                    <DockItem index={4} icon="apps" label="Miễn phí" href="/cong-cu" onClick={go('/cong-cu')} />
                                </span>
                            </Dropdown>
                            <div className="my-1 h-px w-10 bg-slate-200 dark:bg-slate-700" />
                            <DockItem index={5} icon="login" label="Đăng nhập" href="/login" onClick={go('/login')} primary />
                        </motion.nav>
                    </motion.aside>
                ) : (
                    <motion.header key="bar" className="fixed top-4 left-0 right-0 px-4 sm:px-6 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={FADE}>
                        <motion.div
                            layoutId={reduce ? undefined : 'landing-nav'}
                            transition={MORPH}
                            style={{ borderRadius: 32 }}
                            className={`mx-auto max-w-5xl px-4 sm:px-6 py-3 flex items-center justify-between ${shell}`}
                        >
                            <div
                                className="flex items-center gap-2 cursor-pointer"
                                onClick={() => {
                                    navigate('/');
                                    window.scrollTo(0, 0);
                                }}
                            >
                                <img alt="1ms Logo" className="h-10 w-auto" src="/logo.png" />
                            </div>
                            <nav className="hidden md:flex flex-1 justify-center items-center gap-10 text-slate-600 dark:text-slate-300">
                                <a className="text-sm font-bold hover:text-accent transition-colors relative group" href="/#features">
                                    Tính năng
                                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all group-hover:w-full"></span>
                                </a>
                                <a className="text-sm font-bold hover:text-accent transition-colors relative group" href="/#testimonials">
                                    HLV tin dùng
                                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all group-hover:w-full"></span>
                                </a>
                                <a
                                    className="text-sm font-bold hover:text-accent transition-colors relative group"
                                    href="/tra-cuu-phu-huynh"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate('/tra-cuu-phu-huynh');
                                    }}
                                >
                                    Phụ huynh
                                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all group-hover:w-full"></span>
                                </a>
                                <a className="text-sm font-bold hover:text-accent transition-colors relative group" href="/#faq">
                                    Hỏi đáp
                                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all group-hover:w-full"></span>
                                </a>

                                <Dropdown menu={{ items: toolItems }} trigger={['hover']} placement="bottom">
                                    <a
                                        href="/cong-cu"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate('/cong-cu');
                                        }}
                                        className="text-sm font-bold hover:text-accent transition-colors relative group cursor-pointer flex items-center gap-1.5"
                                    >
                                        <AppstoreOutlined className="text-lg" />
                                        Miễn phí <DownOutlined className="text-[10px]" />
                                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all group-hover:w-full"></span>
                                    </a>
                                </Dropdown>
                            </nav>
                            <div className="flex items-center gap-3">
                                {/* Mobile Tools Menu */}
                                {/* Mobile: thêm lối vào cổng phụ huynh ở cuối menu (desktop nằm trên thanh menu) */}
                                <Dropdown menu={{ items: [...toolItems, { type: 'divider' }, parentItem] }} trigger={['click']} placement="bottomRight">
                                    <button className="md:hidden flex items-center gap-1 text-sm font-bold text-slate-600 hover:text-primary transition-colors cursor-pointer bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-full shadow-sm">
                                        Miễn phí <DownOutlined className="text-[10px]" />
                                    </button>
                                </Dropdown>

                                <button
                                    onClick={() => navigate('/login')}
                                    className="rounded-full bg-primary px-5 sm:px-6 py-2 sm:py-2.5 text-sm font-bold text-white shadow-lg shadow-primary/30 hover:bg-primary/90 hover:-translate-y-0.5 transition-all"
                                >
                                    Đăng nhập
                                </button>
                            </div>
                        </motion.div>
                    </motion.header>
                )}
            </AnimatePresence>
            {!isDesktop ? <BackToTop /> : null}
        </LayoutGroup>
    );
};

export default LandingHeader;
